import React, {
  FC, useEffect, useState, useRef, useMemo
} from 'react';
import {
  Alert,
  Checkbox,
  Typography,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  ButtonGroup,
  LoadingIconButton
} from '@one-thd/sui-atomic-components';
import { useListContext } from '../../context/transactable-gift-cards-context';
import { IGiftCardCommonData, IListVariables } from '../../models/common-models';
import { useGiftCardRequest } from '../../hooks/use-gift-card-request';
import { LoadingSpinner } from '../loading-spinner';
import { ERROR } from '../../utils/verbiage';
import {
  TRANSACTABLE_PAGE_SIZE,
  defaultImage,
  MAX_TRANSACTABLE_CARD_LIMIT
} from '../../utils/consts';
import { formatDollar, formatMaskedCardNumber } from '../../utils/general';
import { setViewBarcodeEvent } from '../../utils/analytics';

interface ITransactableGiftCard {
  onConfirm: () => void;
  onClose: () => void;
}

interface IInfiniteScrollList {
  transactableGiftCardList: IGiftCardCommonData[];
  selectedCardIds?: string[];
  pageNumber: number;
  onSelect: (id: string) => void;
  onLoadMore: (page: number) => Promise<void>;
}

const InfiniteScrollList: FC<IInfiniteScrollList> = ({
  transactableGiftCardList,
  selectedCardIds,
  pageNumber,
  onSelect,
  onLoadMore
}) => {
  const maxCardsSelected = (selectedCardIds?.length || 0) >= MAX_TRANSACTABLE_CARD_LIMIT;
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const options = useMemo<IntersectionObserverInit>(
    () => ({
      root: null,
      rootMargin: '0px',
      threshold: 1.0
    }),
    []
  );

  // for debounce purpose
  const requestLockers = useRef(false);
  const lastRequestTimeRef = useRef(0);

  const handleLoadMore = async () => {
    const now = Date.now();
    const minInterval = 300; // Minimum interval between requests in milliseconds
    if (requestLockers.current || now - lastRequestTimeRef.current < minInterval) {
      return;
    }
    requestLockers.current = true;
    lastRequestTimeRef.current = now;
    onLoadMore(pageNumber + 1).then(() => {
      requestLockers.current = false;
    });
  };

  useEffect(() => {
    if (!loaderRef?.current) {
      return;
    }
    const handleObserver: IntersectionObserverCallback = (entries) => {
      const target = entries[0];
      if (target.isIntersecting) {
        handleLoadMore();
      }
    };

    observerRef.current = new IntersectionObserver(handleObserver, options);
    observerRef.current.observe(loaderRef.current);
    // eslint-disable-next-line consistent-return
    return () => {
      observerRef?.current?.disconnect();
    };
  }, [loaderRef, pageNumber]);
  return (
    <>
      {transactableGiftCardList.map((card: IGiftCardCommonData) => {
        const {
          id, nickName, imagePath, availableBalance, cardDetails
        } = card;
        const imageURL = imagePath || defaultImage;
        const isSelected = selectedCardIds?.includes(id);
        const shouldDisableSelection = isSelected ? false : maxCardsSelected;
        const fontColor = shouldDisableSelection ? 'subtle' : 'primary';
        const cardName = nickName || 'Gift Card';
        return (
          <button
            className="sui-flex sui-justify-start sui-items-start"
            onClick={() => onSelect(id)}
            type="button"
            key={id}
            value={id}
            disabled={shouldDisableSelection}
          >
            <Checkbox checked={isSelected} disabled={shouldDisableSelection} />
            <img
              className={`sui-rounded-md sui-pt-2 sui-ml-2 sui-mr-4 ${
                shouldDisableSelection && 'sui-opacity-50'
              }`}
              src={imageURL}
              alt="Placeholder img"
              height="27"
              width="48"
              data-testid="gift-card-image"
            />
            <div className="sui-flex sui-flex-col sui-items-start sui-gap-1 sui-pt-2 sui-grow">
              <Typography weight="bold" color={fontColor}>
                {cardName}
              </Typography>
              {availableBalance && (
                <Typography data-testid="gift-card-balance" color={fontColor}>
                  {formatDollar(availableBalance)}
                </Typography>
              )}
              {cardDetails?.cardNumberLast4 && (
                <Typography color="subtle">
                  {formatMaskedCardNumber(cardDetails.cardNumberLast4)}
                </Typography>
              )}
            </div>
          </button>
        );
      })}
      <div ref={loaderRef} className="sui-invisible">
        Load More Indicator
      </div>
    </>
  );
};

const TransactableGiftCardList: FC<ITransactableGiftCard> = ({ onConfirm, onClose }) => {
  const {
    makeRequest: getCards,
    loading: isGetCardsLoading,
    error
  } = useGiftCardRequest({
    queryName: 'transactableGiftCards'
  });

  const { state, dispatch } = useListContext();
  const { selectedGiftCardPaymentIDs, transactableGiftCardList } = state;
  const [selectedCardIds, setSelectedCardIds] = useState(selectedGiftCardPaymentIDs || []);
  const [pageInfo, setPageInfo] = useState({
    pageNumber: 0,
    totalPages: 0
  });

  const { pageNumber, totalPages } = pageInfo || {};
  const isAllCardsFetched = pageNumber === totalPages && totalPages !== 0;

  const isInitLoading = isGetCardsLoading && pageNumber < 1;
  const isLoadingMore = isGetCardsLoading && pageNumber >= 1;
  const disableConfirm = !selectedCardIds?.length || isLoadingMore;

  const getTransactableCards = async (_page: number) => {
    if (isAllCardsFetched || isGetCardsLoading || _page === pageNumber) return;

    const variables: IListVariables = {
      paginate: {
        pageSize: TRANSACTABLE_PAGE_SIZE.toString(),
        pageNumber: _page.toString()
      }
    };
    await getCards(variables).then((_data) => {
      const cards: IGiftCardCommonData[] = _data?.paymentCards;
      if (cards?.length) {
        const _pageNumber = parseInt(_data?.paginationInfo?.pageNumber, 10);
        const _totalPages = parseInt(_data?.paginationInfo?.totalPages, 10);
        setPageInfo({
          pageNumber: _pageNumber,
          totalPages: _totalPages
        });
        dispatch({
          type: 'SET_LIST',
          payload: [...transactableGiftCardList, ...cards]
        });
      }
    });
  };

  useEffect(() => {
    if (isInitLoading || transactableGiftCardList.length) {
      return;
    }
    getTransactableCards(1);
  }, []);

  const handleSelect = (id: string) => {
    const isSelected = selectedCardIds?.includes(id);
    const updatedSelectedCardIds = isSelected
      ? selectedCardIds.filter((cardId) => cardId !== id)
      : [...(selectedCardIds || []), id];
    setSelectedCardIds(updatedSelectedCardIds);
  };

  const handleConfirm = () => {
    dispatch({ type: 'SELECT_CARD', payload: selectedCardIds || [] });
    setViewBarcodeEvent(selectedCardIds.length);
    onConfirm();
  };

  const renderViewButtonText = () => {
    const count = selectedCardIds?.length || 0;
    if (count === 0) {
      return 'View Barcode';
    }

    if (count === 1) {
      return 'View 1 Barcode';
    }

    return `View ${count} Barcodes`;
  };

  const renderBody = () => {
    if (isInitLoading) {
      return <LoadingSpinner />;
    }

    if (error && !isInitLoading && transactableGiftCardList?.length === 0) {
      return (
        <DrawerBody>
          <Alert status="error">{ERROR.genericError}</Alert>
        </DrawerBody>
      );
    }

    if (transactableGiftCardList?.length === 0 && !isInitLoading) {
      return (
        <DrawerBody>
          <Alert status="error" data-testid="transactable-cards-alert">
            Your saved gift cards don’t have funds left. Reload a card or use a different form of
            payment.
          </Alert>
        </DrawerBody>
      );
    }

    return (
      <>
        <div className="sui-h-full sui-flex sui-flex-col sui-overflow-hidden sui-bg-primary sui-p-4">
          <div className="sui-pb-4">
            <Typography>Select which gift card(s) you’d like to scan for payment.</Typography>
            <Typography color="subtle" variant="body-sm">
              You may select up to 10 gift cards.
            </Typography>
          </div>
          <div
            className="sui-flex sui-flex-col sui-gap-y-2 sui-overflow-auto sui-grow"
            data-testid="gift-card-list"
          >
            <InfiniteScrollList
              transactableGiftCardList={transactableGiftCardList}
              selectedCardIds={selectedCardIds}
              onSelect={handleSelect}
              pageNumber={pageNumber}
              onLoadMore={getTransactableCards}
            />
          </div>
          {isLoadingMore && (
            <div className="sui-flex sui-justify-center sui-items-center">
              <LoadingIconButton loading />
            </div>
          )}
        </div>
        <DrawerFooter>
          <ButtonGroup orientation="vertical">
            <Button variant="primary" onClick={handleConfirm} disabled={disableConfirm}>
              {renderViewButtonText()}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </ButtonGroup>
        </DrawerFooter>
      </>
    );
  };

  return (
    <>
      <DrawerHeader title="Select Gift Cards" onClose={onClose} />
      {renderBody()}
    </>
  );
};

export { TransactableGiftCardList, InfiniteScrollList };
